import { Box } from '@chakra-ui/react';
import { InnerDrawer } from '@pluxee-design-system/core';
import {
  getPriceAndFees,
  hasPaperVoucherWarning,
  PriceAndFees,
} from 'components/Products/PayoutCard/utils';
import { PayoutChannel, PayoutFrequency, ProductFull } from 'generated/models';
import useTranslations from 'i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LocationsFooter from '../LocationsFooter';
import { ChangeLocations, CommonProduct, PartnerAccountsDict, PayoutsFormValues } from '../types';
import LocationsStep from './LocationsStep';
import PayoutsStep from './PayoutsStep';
import { getSelectedPayout } from './utils';

interface ProductDrawerProps {
  // permissions
  isLocationsStepEditable?: boolean; // can edit assigned locations via LocationsStep?
  isPayoutSelectable?: boolean; // can select a payout?
  isPayoutsStepEditable?: boolean; // can edit payout via PayoutsStep?
  isProductDetachable?: boolean; // can remove product?
  withPayouts?: boolean; // visibility of PayoutsStep
  withLocations?: boolean; // visibility of LocationsStep
  // rest
  product: CommonProduct;
  partnerAccounts: PartnerAccountsDict;
  removeText?: string;
  selectedPayoutId?: string;
  onRemove?: (product: CommonProduct, productName?: string) => void;
  onSelect: (
    product: CommonProduct,
    payout?: PayoutChannel,
    locations?: ChangeLocations,
    onlyLocations?: boolean,
    accountNumber?: string,
    productName?: string,
    originalPayoutId?: string,
  ) => void;
  onClose: () => void;
  locationCount?: number;
  // tracking
  onTrackView?: (productName: string) => void;
  onTrackPayoutApply?: (productName: string) => void;
  onTrackRemove?: (productName: string) => void;
  onTrackManageLocations?: (productName: string) => void;
  onTrackPayoutFrequency?: (productName: string, frequency: string) => void;
  onTrackPayoutOfferSelect?: (
    productName: string,
    fees: PriceAndFees,
    frequency: string,
    channel: string,
    tier?: string,
  ) => void;
}

enum ViewTypeEnum {
  Payouts = 'payouts',
  Locations = 'locations',
}

const ProductDrawer = ({
  isLocationsStepEditable = true,
  isPayoutSelectable = true,
  isPayoutsStepEditable = true,
  isProductDetachable = true,
  product,
  product: { code: productId, nameKey: productNameKey },
  partnerAccounts,
  removeText,
  selectedPayoutId: originalPayoutId,
  onClose,
  onRemove,
  onSelect,
  locationCount = 0,
  withPayouts = true,
  withLocations = false,
  onTrackView,
  onTrackRemove,
  onTrackManageLocations,
  onTrackPayoutApply,
  onTrackPayoutFrequency,
  onTrackPayoutOfferSelect,
}: ProductDrawerProps) => {
  const { t, tQueryKey } = useTranslations();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const productName = t('product_details.products.*.product.name', product.name, {
    query: { where: 'product_details.products.*.product.code', is: product.nameKey },
  });
  const payoutList = (product as ProductFull)?.payoutList;
  const payouts = useMemo(
    () =>
      (payoutList || []).reduce(
        (result, current) => {
          result[String(current.nameKey)] = current;
          return result;
        },
        {} as Record<string, PayoutFrequency>,
      ),
    [payoutList],
  );
  const [selectedPayout, setSelectedPayout] = useState(() =>
    getSelectedPayout(payoutList, originalPayoutId),
  );
  // TODO: somehow initialize based on selected product if defined
  const [selectedLocationCount, setSelectedLocationCount] = useState<number>(locationCount);
  const [selectedLocations, setSelectedLocations] = useState<ChangeLocations | undefined>(
    undefined,
  );
  const [viewType, setViewType] = useState(
    withPayouts ? ViewTypeEnum.Payouts : ViewTypeEnum.Locations,
  );
  const onShowPayouts = useCallback(() => setViewType(ViewTypeEnum.Payouts), [setViewType]);
  const onShowLocations = useCallback(() => {
    onTrackManageLocations?.(productName);
    setViewType(ViewTypeEnum.Locations);
  }, [onTrackManageLocations, productName, setViewType]);
  const onRemoveProduct = useMemo(
    () =>
      onRemove
        ? () => {
            onTrackRemove?.(productName);
            onRemove(product, productName);
          }
        : undefined,
    [onRemove, onTrackRemove, product, productName],
  );
  const handleTrackPayoutFrequency = useCallback(
    (frequency: string) => onTrackPayoutFrequency?.(productName, frequency),
    [onTrackPayoutFrequency, productName],
  );

  const handleSelectPayout = useCallback(
    (newPayout: PayoutChannel) => {
      if (onTrackPayoutOfferSelect && newPayout) {
        const tQueryProduct = tQueryKey('product_details.products.*.product', {
          where: 'product_details.products.*.product.code',
          is: product.nameKey,
        });
        const tQueryTier = tQueryKey(`${tQueryProduct}.tiers.*.tier`, {
          where: `${tQueryProduct}.tiers.*.tier.code`,
          is: newPayout.priceTierName,
        });

        onTrackPayoutOfferSelect(
          t(`${tQueryProduct}.name`, product.name),
          getPriceAndFees(newPayout.priceList),
          newPayout.frequencyIndividual
            ? t('product_details.payoutFrequency.individual', 'Individual')
            : t(
                `product_details.payoutFrequency.${newPayout.frequencyNameKey}`,
                newPayout.frequencyName,
              ),
          t(`product_details.payouts.*.${newPayout.nameKey}.name`, newPayout.name),
          t(`${tQueryTier}.name`, newPayout.priceTierName),
        );
      }
      setSelectedPayout(newPayout);
    },
    [onTrackPayoutOfferSelect, product.name, product.nameKey, t, tQueryKey],
  );

  const handlePayoutsSubmit = useCallback(
    (values: PayoutsFormValues) => {
      onTrackPayoutApply?.(productName);
      onSelect(
        product,
        selectedPayout,
        selectedLocations,
        !isPayoutsStepEditable,
        values.partnerAccounts[values.parameterCode] ?? undefined,
        productName,
        originalPayoutId,
      );
      onClose();
    },
    [
      isPayoutsStepEditable,
      onClose,
      onSelect,
      onTrackPayoutApply,
      originalPayoutId,
      product,
      productName,
      selectedLocations,
      selectedPayout,
    ],
  );
  const handleLocationsSubmit = useCallback(
    (toAdd: string[], toRemove: string[]) => {
      setSelectedLocations({ toAdd, toRemove });
      setSelectedLocationCount(locationCount + toAdd.length - toRemove.length);
      setViewType(ViewTypeEnum.Payouts);
    },
    [locationCount],
  );
  const handleOnlyLocationsSubmit = useCallback(
    (toAdd: string[], toRemove: string[]) => {
      onSelect(product, undefined, { toAdd, toRemove }, true, undefined);
      onClose();
    },
    [onClose, onSelect, product],
  );
  // all events (Esc, click outside) are propagated to InnerDrawer, it's an only way to detect close inside Modal
  // Esc in Modal doesn't work as it is global window's event
  const handleClose = useCallback(
    (e: UIEvent) => {
      if (wrapperRef.current && e?.target && wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    },
    [onClose],
  );
  const hasChangedPayout =
    selectedPayout !== undefined && originalPayoutId !== selectedPayout.payoutUid;
  const hasChangedLocations =
    selectedLocations !== undefined &&
    (selectedLocations.toAdd.length > 0 || selectedLocations.toRemove.length > 0);

  useEffect(() => {
    setSelectedPayout(getSelectedPayout(payoutList, originalPayoutId));
  }, [payoutList, originalPayoutId, setSelectedPayout]);

  useEffect(() => {
    setViewType(withPayouts ? ViewTypeEnum.Payouts : ViewTypeEnum.Locations);
  }, [setViewType, productId, withPayouts]);

  useEffect(() => {
    setSelectedLocationCount(locationCount);
    setSelectedLocations(undefined);
  }, [productId, locationCount, setSelectedLocationCount, setSelectedLocations]);

  useEffect(() => {
    onTrackView?.(productName);
  }, [onTrackView, productName]);

  return (
    <Box ref={wrapperRef}>
      <InnerDrawer title={{ text: productName }} isOpen={true} onClose={handleClose}>
        {viewType === ViewTypeEnum.Payouts && withPayouts && (
          <PayoutsStep
            isEditable={isPayoutsStepEditable}
            isSelectable={isPayoutSelectable}
            isSubmittable={isPayoutsStepEditable || (isLocationsStepEditable && withLocations)}
            isStepValid={
              isPayoutSelectable
                ? (isPayoutsStepEditable && hasChangedPayout) ||
                  (isLocationsStepEditable && hasChangedLocations)
                : isLocationsStepEditable && hasChangedLocations
            }
            onSelectPayout={handleSelectPayout}
            onSubmit={handlePayoutsSubmit}
            onShowLocations={onShowLocations}
            payouts={payouts}
            partnerAccounts={partnerAccounts}
            productNameKey={productNameKey}
            selectedPayout={selectedPayout}
            showWarning={hasPaperVoucherWarning(product, selectedPayout)}
            withLocations={withLocations}
            onTrackPayoutFrequency={handleTrackPayoutFrequency}
          >
            <LocationsFooter
              removeProductText={removeText}
              selectedLocations={selectedLocationCount}
              onRemoveProduct={isProductDetachable ? onRemoveProduct : undefined}
              onShowLocations={withLocations ? onShowLocations : undefined}
            />
          </PayoutsStep>
        )}
        {viewType === ViewTypeEnum.Locations && withLocations && (
          <LocationsStep
            isEditable={isLocationsStepEditable}
            selectedLocations={selectedLocations}
            productId={productId}
            onBack={withPayouts ? onShowPayouts : undefined}
            onSubmit={withPayouts ? handleLocationsSubmit : handleOnlyLocationsSubmit}
          />
        )}
      </InnerDrawer>
    </Box>
  );
};

export default ProductDrawer;
